import { useQuery } from '@apollo/client';
import { addUFOCustomCohortData } from '@atlaskit/react-ufo/custom-cohort-data';
import { useSetStatsigUser, useStatsig } from '@atlassian/mpac-feature-flags';
import { LocalStorageUtils } from '@atlassian/mpac-utils';
import { userType } from '@atlassiansox/analytics-web-client';
import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo } from 'react';

import * as analytics from '../../../common/utils/analytics/analytics';
import bootstrapConfig from '../../bootstrapConfig';
import { getManageDeveloperSpaceLink } from './utils';

import type { CurrentUserContextType, LoggedInUser } from '.';
import type { GetCurrentUserQuery, GetCurrentUserQueryVariables } from './CurrentUser.gql.types';
import type { ReactNode } from 'react';

import { GetCurrentUser } from './CurrentUser.graphql';

const CurrentUserContext = createContext<CurrentUserContextType>({
  loading: false,
});

type Props = {
  children: ReactNode;
};

export const CurrentUserProvider = ({ children }: Props) => {
  const { enableCurrentUserApiViaStoreBff } = useStatsig();
  const setStatsigUser = useSetStatsigUser();
  // Memoize to prevent new reference on every render
  const analyticsClient = useMemo(() => bootstrapConfig.get().analyticsClient, []);
  const vendorId = useMemo(() => {
    return LocalStorageUtils.getFromStorage('last-visited-partner-id');
  }, []);

  const { data, loading } = useQuery<GetCurrentUserQuery, GetCurrentUserQueryVariables>(
    GetCurrentUser,
    {
      skip: !enableCurrentUserApiViaStoreBff(),
      variables: {
        vendorId,
      },
    }
  );

  // Extract identity values first to avoid complex expressions in dependency arrays
  const userTypeName = data?.marketplaceStore?.currentUser?.__typename;
  const userId =
    userTypeName === 'MarketplaceStoreLoggedInUser'
      ? (data?.marketplaceStore?.currentUser as { id: string }).id
      : null;
  const lastVisitedVendorId =
    userTypeName === 'MarketplaceStoreLoggedInUser'
      ? (
          data?.marketplaceStore?.currentUser as {
            lastVisitedDeveloperSpace?: { vendorId?: string };
          }
        ).lastVisitedDeveloperSpace?.vendorId
      : undefined;

  // Memoize the entire data to prevent re-renders from Apollo returning new object references
  // We depend only on identity primitives, not the full data object
  const stableData = useMemo(
    () => data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userTypeName, userId, lastVisitedVendorId]
  );

  const currentUser = stableData?.marketplaceStore?.currentUser;

  const currentUserContext = useMemo<CurrentUserContextType>(() => {
    if (loading) {
      return { loading: true };
    }

    if (!currentUser) {
      return { loading: false };
    }

    if (currentUser.__typename === 'MarketplaceStoreLoggedInUser') {
      const loggedInUser: LoggedInUser = {
        ...currentUser,
        loggedInUserLinks: {
          ...currentUser.loggedInUserLinks,
          manageDeveloperSpace: getManageDeveloperSpaceLink(lastVisitedVendorId),
        },
      };

      return { loggedInUser, loading: false };
    } else {
      return { loading: false, anonymousUser: currentUser };
    }
  }, [loading, currentUser, lastVisitedVendorId]);

  useLayoutEffect(() => {
    if (userTypeName) {
      addUFOCustomCohortData('is_user_logged_in', userTypeName === 'MarketplaceStoreLoggedInUser');
    }
  }, [userTypeName]);

  useEffect(() => {
    // Use userId directly from primitive extraction to avoid depending on context object
    if (userId) {
      analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
      analytics.markHasLoggedIn();
      setStatsigUser({ atlassianAccountId: userId });
    }
    // analyticsClient and setStatsigUser are stable references from useMemo/hooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return (
    <CurrentUserContext.Provider value={currentUserContext}>{children}</CurrentUserContext.Provider>
  );
};

export const useCurrentUser = (): CurrentUserContextType => {
  return useContext(CurrentUserContext);
};
